import { css } from "@emotion/react";
import { useState, useCallback } from "react";
import { dispatch, useSelector } from "src/redux/store";
import { useDropzone } from "react-dropzone";
import { STEPS } from "src/utils/constant";
import {
  reset,
  setBulkUplodAIType,
  setCsvFileStep,
  setUploadedSuccesfully,
  updateShowDialog,
} from "src/redux/reducers/file-upload.reducer";
import { useRouter } from "next/router";
import GlobalAPI from '@/modules/batchEditor/services/GlobalAPI';
import useToast from "@/hooks/useToast";
import NewSuccessDialog from './NewSuccessDialog';
import { Button, CircularProgress } from "@mui/material";
import { EditorScreen } from "@/modules/batchEditor/BulkUploadEditor/constant";
import { aiTaskDisableAtom, batchEditorSelectedPresetAtom } from "../batchEditor/atoms";
import BatchEditorSuccessDialog from "@/modules/file-upload/batchEditorSucessDialog";
import { fileCountAtom } from "../batchEditor/atoms";
import { useSetAtom,useAtomValue } from "jotai";
import SelectGenerationModel from './SelectGenerationModel';
import { BULK_AI_TYPE } from "@/utils/toolsConstants";
import { localConvertToCSV, removeExtension } from "src/utils/helpers";
import { processCSV } from "./homepageUpload";

export function containsVideoLink(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const rows = content.split("\n").map((row) => row.split(","));
      const headers = rows[0];
      const videoExtensions = [".mp4", ".webm", ".mov", ".avi"];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        for (let j = 0; j < headers.length; j++) {
          const columnName = headers[j];
          if (columnName.startsWith("Image") && row[j]) {
            const url = row[j].trim();
            if (videoExtensions.some((ext) => url.toLowerCase().includes(ext))) {
              resolve(true);
              return;
            }
          }
        }
      }

      resolve(false);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsText(file);
  });
}

export function hasLongImageUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const maxImage = process.env.NEXT_PUBLIC_MAX_CSV_ROW_IMAGE_LIMIT || 100;
      const content = event.target.result;
      const rows = content.split("\n").map((row) => row.split(","));
      const headers = rows[0];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        let imageUrlCount = 0;

        for (let j = 0; j < headers.length; j++) {
          const columnName = headers[j];
          if (columnName.startsWith("Image") && row[j]) {
            imageUrlCount++;
          }
        }
        if (imageUrlCount > maxImage) {
          resolve(true);
          return;
        }
      }

      resolve(false);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsText(file);
  });
}

export function hasMoreImageUrls(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const maxImage = process.env.NEXT_PUBLIC_MAX_AI_TASK_ENABLE_FILE_LIMIT || 20;
      const content = event.target.result;

      const rows = content.split("\n").filter((row) => row.trim() !== "");
      const headers = rows[0].split(",").map((header) => header.trim());

      let totalImageUrlCount = 0;

      for (let i = 1; i < rows.length; i++) {
        const rowValues = rows[i].split(",").map((value) => value.trim());
        let rowHasImage = false;

        for (let j = 0; j < headers.length; j++) {
          const columnName = headers[j];

          if (columnName.startsWith("Image") && rowValues[j]) {
            totalImageUrlCount++;
            rowHasImage = true;

            if (totalImageUrlCount > maxImage) {
              resolve(`Upload up to ${maxImage} images allowed`);
              return;
            }
          }
        }

        if (!rowHasImage) {
          resolve(`Row ${i} has no valid image URLs`);
          return;
        }
      }

      resolve(false);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsText(file);
  });
}


const CsvFileUploadStep = () => {
  const [csvFile, setCSVFile] = useState();
  const [csvInstructionFile, setCSVInstructionFile] = useState();
  const [error, setError] = useState(null);
  const [csvInstructionError, setCSVInstructionError] = useState(null);
  const router = useRouter();
  const toast = useToast();
  const [uploading,setUploading] = useState(false);
  const [removeBackground,setRemoveBackground] = useState(false);
  const batchEditorSelectedPreset = useAtomValue(batchEditorSelectedPresetAtom);
  const [openFileUploadSuccessDialog, closeFileUploadSuccessDialog] = useState(false);
  const [openSelectionDialog, setOpenSelectionDialog] = useState(false);
  const setFileCount = useSetAtom(fileCountAtom);
  const {csvFileStep, bulkUplodAIType} = useSelector(state => state.fileUpload);
  const { entireStrapiData } = useSelector((state) => state.strapi);
  const createProjectData =
    entireStrapiData?.StudioBatchEditorText?.StudioBatchEditorText;
  const aiTaskDisable = useAtomValue(aiTaskDisableAtom);

  const saveProject = async(e) => {
      e.preventDefault();
      setFileCount(null);
      closeFileUploadSuccessDialog(true);
  };

  const selectionOptionHandler = (value) => {
    setRemoveBackground(value);
    setOpenSelectionDialog(true);
  };

  const submitHandler = async(selectionData) => {
    setUploading(true);
    try{
    const hasVideoLink = await containsVideoLink(csvFile);
    if(hasVideoLink && !aiTaskDisable){
      toast.info("Cannot process CSV with video links. Please remove them!");
      dispatch(reset());
      dispatch(updateTitle("Create Project"));
      dispatch(updateStep(STEPS?.INIT));
      setUploading(false);
      return;
    }
    const imageLimitCrossed = await hasLongImageUrl(csvFile);
    if(imageLimitCrossed){
      toast.info("Max image limit exceeded. Remove images.");
      dispatch(reset());
      dispatch(updateTitle("Create Project"));
      dispatch(updateStep(STEPS?.INIT));
      setUploading(false);
      return;
    }
    const formData = new FormData();
    const csvContent = await csvFile.text();
    const fileName = removeExtension(csvFile?.name);
    const jsonOutput = await processCSV(csvContent,fileName);
    if (!jsonOutput || jsonOutput.length === 0) {
        return;
    }
    const processedCSV = localConvertToCSV(jsonOutput);
    formData.append("file", new Blob([processedCSV], { type: 'text/csv' }), fileName);
    formData.append("uploadType", "csv");
    formData.append("marketplaceId",batchEditorSelectedPreset?.marketplace_id)
    bulkUplodAIType && formData.append("batchType",bulkUplodAIType);
    csvInstructionFile && formData.append("instructionFile", csvInstructionFile);
    formData.append("removeBackground",removeBackground);
    selectionData?.modelSelection && formData.append("modelSelection",JSON.stringify(selectionData?.modelSelection));
    // instructionType && formData.append("instructionType",instructionType);
    await GlobalAPI.uploadBulkJobCSV(formData).then((response)=>{
      if(response.data?.data?.batchId){
      dispatch(setCsvFileStep(STEPS.CSV_FILE_STEP_2));
      dispatch(setUploadedSuccesfully(0));
      let mode = EditorScreen.MAIN;
      if (aiTaskDisable) {
        mode = EditorScreen.PRODUCT_LIFESTYLE;
        router.replace({
          query: { mode, batchAI: response.data?.data?.batchId},
        });
      } else {
        router.push(`/dashboard/batch-editor?mode=${EditorScreen.GENERATE}&batchAI=${response.data?.data?.batchId}`);
      }
      dispatch(setBulkUplodAIType(''));
    }else{
      toast.error("Something went wrong")
    }
    }).catch(error=>{
      const message = error.response.data.message.csvError || error.response.data.errorResponse || 'CSV upload failed'
      toast.error(message)
      setUploading(false);
    })
    onCloseHandler();
    setUploading(false);
   }catch{(error)=>{
    console.error('CSV upload  error',error)
    setUploading(false);
   }}
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles?.length) return;
      if (acceptedFiles.length !== 1) {
        setError("Please upload exactly one CSV file");
      } else {
        setCSVFile(acceptedFiles[0]);
        setError(null);
      }
    },
    [setError]
  );

  const onDropInstruction = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles?.length) return;
      if (acceptedFiles.length !== 1) {
        setCSVInstructionError("Please upload exactly one CSV file");
      } else {
        setCSVInstructionFile(acceptedFiles[0]);
        setCSVInstructionError(null);
      }
    },
    [setCSVInstructionError]
  );

  const onCloseHandler=()=>{
    dispatch(reset());
    dispatch(updateShowDialog(false));
  }
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
  });

  const { getRootProps:getInstructionRootProps, getInputProps:getInstructionInputProps, isDragActive:isDragActiveProps } = useDropzone({
    onDrop: onDropInstruction,
    accept: { "text/csv": [".csv"] },
  });

  const  handleDownloadModelSample = () => {
    const link = document.createElement('a');
    link.href = process.env.NEXT_PUBLIC_BULK_UPLOAD_CSV_MODEL_URL;
    link.download = 'BulkUploadSample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const  handleDownloadSample = () => {
    const link = document.createElement('a');
    link.href = process.env.NEXT_PUBLIC_BULK_UPLOAD_CSV_URL;
    link.download = 'BulkUploadSample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const  handleInstructionDownloadSample = () => {
    const link = document.createElement('a');
    link.href = process.env.NEXT_PUBLIC_BULK_UPLOAD_INSTRUCTION_CSV_URL;
    link.download = 'BulkUploadSample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
    {csvFileStep === STEPS.CSV_FILE && <div css={styles.container}>
      <form onSubmit={saveProject}>
        <div {...getRootProps()} style={styles.dropzoneStyles}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <p>Drop the CSV file here ...</p>
          ) : (
            <p style={styles.dropzoneStylesText}>
              {csvFile?.name ||
                `Drag 'n' drop a CSV file here`}
            </p>
          )}
          {error && <h3 css={styles.errorMsg}>{error}</h3>}
        </div>
        {bulkUplodAIType !== BULK_AI_TYPE.BULK_UPLOAD_FACE_SWAP && <div css={styles.optionContainer}>
        <div {...getInstructionRootProps()} style={styles.dropzoneStyles}>
          <input {...getInstructionInputProps()} />
          {isDragActiveProps ? (
            <p>Add Instruction CSV file here ...</p>
          ) : (
            <p style={styles.dropzoneStylesText}>
              {csvInstructionFile?.name ||
                `Drag 'n' drop Instruction CSV file here`}
            </p>
          )}
          {csvInstructionError && <h3 css={styles.errorMsg}>{csvInstructionError}</h3>}
        </div>
        </div>}
        <button
          type="Submit"
          disabled={error || !csvFile || uploading}
          css={styles.continueBtn}
        >
          {uploading ? <CircularProgress color={'inherit'} size={24} /> :createProjectData?.ContinueCTA || `Continue`}
        </button>
      </form>
      <div>
      <Button onClick={handleDownloadSample} sx={styles.sampleCss}  variant="text"> Project Sample </Button>
      {bulkUplodAIType !== BULK_AI_TYPE.BULK_UPLOAD_FACE_SWAP && <Button onClick={handleInstructionDownloadSample} sx={styles.sampleCss}  variant="text"> Instruction Sample </Button>}
      </div>
    </div>}
    {csvFileStep === STEPS.CSV_FILE_STEP_2 && <NewSuccessDialog 
        open={true}
        disableBtn={false}
        disableCredit={true}
        onClose={onCloseHandler}
        successText={csvFile?.name}
        message={`After your projects have been Created, You can track the progress of your order on the 'My Projects' tab.`}/> 
      }
      <BatchEditorSuccessDialog 
      open={openFileUploadSuccessDialog}
      onClose={()=>closeFileUploadSuccessDialog(false)}
      saveProject={selectionOptionHandler}
      />
      <SelectGenerationModel
      open={openSelectionDialog}
      onClose={()=>setOpenSelectionDialog(false)}
      saveProject={submitHandler}
       />
    </div>
  );
};

const styles = {
  container: css`
    height: 340px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items:center;
    form {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  `,
  continueBtn: css`
    width: 100%;
    height: 50px;
    background: #262626;
    color: #fff;
    cursor: pointer;
    min-width: 485px;
    border-radius: 4px;
    :disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,
  errorMsg: css`
    color: red;
    font-size: 12px;
  `,
  optionContainer:css`
    display:flex;
  `,
  dropzoneStyles: {
    border: "2px dashed #cccccc",
    borderRadius: "4px",
    padding: "20px",
    textAlign: "center",
    cursor: "pointer",
    margin: "20px",
  },
  dropzoneInstructionStyles: {
    border: "2px dashed #EEEEEE",
    borderRadius: "4px",
    padding: "10px",
    cursor: "pointer",
    margin: "10px",
    opacity:0.8,
    maxWidth:"360px"
  },
  dropzoneStylesText:{
    whiteSpace: "nowrap",
    width:"340px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  sampleCss:{
    marginTop: '20px',
  },
  mb20: {
    margin: '20px',
  }
};
export default CsvFileUploadStep;
